import React, { useState, useEffect } from "react";
import PageHeader from "./reporting/PageHeader";
import IntegrationStatus from "./reporting/IntegrationStatus";
import SummaryCards from "./reporting/SummaryCards";
import Charts from "./reporting/Charts";
import ReportsTable from "./reporting/ReportsTable";
import SchoolSelector from "./reporting/SchoolSelector";
import supabase from "../../../utils/supabase";

const AnalyticsDashboard = () => {
  const [selectedSchoolIds, setSelectedSchoolIds] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(true);

  const fetchSchools = async () => {
    try {
      setSchoolsLoading(true);
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("district")
        .eq("id", user.id)
        .single();

      if (!profile?.district) return;

      const { data, error } = await supabase
        .from("schools")
        .select("id, name")
        .eq("district_id", profile.district);

      if (error) {
        console.error("Error fetching schools:", error);
        return;
      }

      setSchools(data || []);
    } catch (error) {
      console.error("Error fetching schools:", error);
    } finally {
      setSchoolsLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setLoading(true);

      const [
        { data: summaryData, error: summaryError },
        { data: chartData, error: chartError },
      ] = await Promise.all([
        supabase.functions.invoke("getAdminDashboardSummary", {
          body: JSON.stringify({
            schoolIds: selectedSchoolIds || [],
          }),
        }),
        supabase.functions.invoke("getAdminReportAndAnalytics", {
          body: JSON.stringify({
            schoolIds: selectedSchoolIds || [],
          }),
        }),
      ]);

      if (summaryError) {
        throw summaryError;
      }
      if (chartError) {
        throw chartError;
      }

      setAnalytics(summaryData);
      setChartData(chartData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch analytics"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [selectedSchoolIds]);

  const handleSchoolIdsChange = (schoolIds) => {
    setSelectedSchoolIds(schoolIds);
  };

  return (
    <div className="font-sans bg-gray-50">
      <PageHeader />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <IntegrationStatus />

        <div className="bg-white p-6 rounded shadow text-gray-600">
          <>
            <SchoolSelector
              schools={schools}
              schoolsLoading={schoolsLoading}
              selectedSchoolIds={selectedSchoolIds}
              onSchoolIdsChange={handleSchoolIdsChange}
            />
            <SummaryCards
              analytics={analytics}
              loading={loading}
              error={error}
            />
            <Charts chartData={chartData} loading={loading} error={error} />
            <ReportsTable />
          </>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;
